import { useCallback, useEffect, useState } from 'react';
import { get, post } from '../lib/api';
import { Avatar, ErrorNote, Empty, Spinner } from '../components/ui';
import PayoutSheet from '../components/PayoutSheet';
import ExtrasManager from '../components/ExtrasManager';
import { formatMinutes, money, percent, periodLabel } from '../lib/format';
import type { Claim, OverviewChild, PeriodSummary } from '../lib/types';

type Row = Omit<PeriodSummary, 'items'>;

interface Entry {
  child: OverviewChild['child'];
  rows: Row[];
}

/** "Do they get their pocket money?" — the last few periods, one tap to decide. */
export default function ParentReview(): JSX.Element {
  const [periodType, setPeriodType] = useState<'week' | 'month'>('week');
  const [entries, setEntries] = useState<Entry[] | null>(null);
  const [picked, setPicked] = useState<{ childId: number; row: Row } | null>(null);
  const [pending, setPending] = useState<Claim[]>([]);
  const [owed, setOwed] = useState<Claim[]>([]);
  const [children, setChildren] = useState<OverviewChild['child'][]>([]);
  const [currency, setCurrency] = useState('');
  const [managing, setManaging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPending = useCallback(async () => {
    const [waiting, unpaid] = await Promise.all([
      get<{ claims: Claim[] }>('/api/extra-claims?status=pending'),
      get<{ claims: Claim[] }>('/api/extra-claims?status=owed'),
    ]);
    setPending(waiting.claims);
    setOwed(unpaid.claims);
  }, []);

  async function decide(claim: Claim, status: 'approved' | 'rejected', paid = false) {
    const note = status === 'rejected' ? prompt('Tell them why (optional):') ?? '' : '';
    await post(`/api/extra-claims/${claim.id}/decide`, { status, note, paid });
    await Promise.all([loadPending(), load()]);
  }

  async function markPaid(claim: Claim) {
    await post(`/api/extra-claims/${claim.id}/paid`, { paid: true });
    await Promise.all([loadPending(), load()]);
  }

  async function payAll(childClaims: Claim[]) {
    const total = childClaims.reduce((sum, claim) => sum + claim.amount, 0);
    if (!confirm(`Mark ${childClaims.length} extras (${money(total, currency)}) as paid to ${childClaims[0].child_name}?`)) return;
    for (const claim of childClaims) await post(`/api/extra-claims/${claim.id}/paid`, { paid: true });
    await Promise.all([loadPending(), load()]);
  }

  const load = useCallback(async () => {
    setEntries(null);
    try {
      const overview = await get<{ children: OverviewChild[]; settings: { currency: string } }>('/api/overview');
      setChildren(overview.children.map((c) => c.child));
      setCurrency(overview.settings.currency);
      const result = await Promise.all(
        overview.children.map(async (entry) => ({
          child: entry.child,
          rows: (
            await get<{ history: Row[] }>(
              `/api/history?childId=${entry.child.id}&periodType=${periodType}&count=4`,
            )
          ).history,
        })),
      );
      setEntries(result);
    } catch (err) {
      setError((err as Error).message);
    }
  }, [periodType]);

  useEffect(() => {
    void load();
    void loadPending().catch((err) => setError((err as Error).message));
  }, [load, loadPending]);

  return (
    <>
      <h1>Pocket money</h1>
      <ErrorNote error={error} />

      {pending.length > 0 && (
        <>
          <h2>Extras to approve</h2>
          <div className="stack">
            {pending.map((claim) => (
              <div key={claim.id} className="card tight">
                <div className="row">
                  <Avatar emoji={claim.child_emoji} />
                  <div className="grow">
                    <strong>{claim.child_name}</strong> · {claim.icon} {claim.title}
                    {claim.minutes ? <span className="muted"> · {formatMinutes(claim.minutes)}</span> : null}
                    <div className="small muted">“{claim.note}”</div>
                  </div>
                  <strong style={{ whiteSpace: 'nowrap' }}>{money(claim.amount, currency)}</strong>
                </div>
                <div className="row" style={{ marginTop: 10 }}>
                  <button className="btn danger small" type="button" onClick={() => void decide(claim, 'rejected')}>Reject</button>
                  <button className="btn secondary small grow" type="button" onClick={() => void decide(claim, 'approved')}>Approve</button>
                  <button className="btn small grow" type="button" onClick={() => void decide(claim, 'approved', true)}>Approve & paid</button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {owed.length > 0 && (
        <>
          <h2>Approved, not paid yet</h2>
          <p className="tiny muted" style={{ marginTop: -6 }}>
            Mark them paid as you hand the money over. Anything still here when you pay the week is
            included in that payment and marked paid with it.
          </p>
          <div className="stack">
            {Object.values(
              owed.reduce<Record<number, Claim[]>>((byChild, claim) => {
                (byChild[claim.child_id] ??= []).push(claim);
                return byChild;
              }, {}),
            ).map((childClaims) => {
              const total = childClaims.reduce((sum, claim) => sum + claim.amount, 0);
              return (
                <div key={childClaims[0].child_id} className="card tight">
                  <div className="row between">
                    <span className="row">
                      <Avatar emoji={childClaims[0].child_emoji} />
                      <strong>{childClaims[0].child_name}</strong>
                    </span>
                    <span className="row">
                      <strong>{money(total, currency)}</strong>
                      {childClaims.length > 1 && (
                        <button className="btn small" type="button" onClick={() => void payAll(childClaims)}>
                          All paid
                        </button>
                      )}
                    </span>
                  </div>
                  {childClaims.map((claim) => (
                    <div key={claim.id} className="row between" style={{ marginTop: 8 }}>
                      <span className="small grow">
                        {claim.icon} {claim.title}
                        {claim.minutes ? <span className="muted"> · {formatMinutes(claim.minutes)}</span> : null}
                        <span className="tiny muted" style={{ display: 'block' }}>
                          {claim.claimed_on} · {money(claim.amount, currency)}
                        </span>
                      </span>
                      <button className="btn secondary small" type="button" onClick={() => void markPaid(claim)}>
                        Paid
                      </button>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </>
      )}

      <div className="row between" style={{ marginTop: 14 }}>
        <span className="small muted">Chores and extras, by period</span>
        <button className="btn secondary small" type="button" onClick={() => setManaging(true)}>⭐ Manage extras</button>
      </div>
      <div className="chips" style={{ margin: '10px 0 14px' }}>
        {(['week', 'month'] as const).map((option) => (
          <button
            key={option}
            type="button"
            className={`chip${periodType === option ? ' on' : ''}`}
            onClick={() => setPeriodType(option)}
          >
            {option === 'week' ? 'Weekly' : 'Monthly'}
          </button>
        ))}
      </div>

      {!entries ? (
        <Spinner />
      ) : entries.length === 0 ? (
        <Empty icon="🧒">No kids yet.</Empty>
      ) : (
        <div className="stack">
          {entries.map((entry) => (
            <div key={entry.child.id} className="card">
              <div className="row" style={{ marginBottom: 10 }}>
                <Avatar emoji={entry.child.emoji} />
                <strong className="grow">{entry.child.name}</strong>
              </div>
              <div className="stack">
                {entry.rows.map((row) => {
                  const decided = row.payout;
                  return (
                    <button
                      key={row.periodKey}
                      type="button"
                      className="chore"
                      onClick={() => setPicked({ childId: entry.child.id, row })}
                    >
                      <span className="grow">
                        <span className="chore-title">
                          {periodLabel(periodType, row.periodKey)}
                          {row.provisional && <span className="tiny muted"> · in progress</span>}
                        </span>
                        <span className="tiny muted line">
                          {row.applicable
                            ? `${row.doneCount}/${row.totalCount} chores · ${percent(row.rate)} (needs ${percent(row.threshold)})`
                            : 'Nothing was due'}
                          {row.extrasApproved > 0 && ` · +${money(row.extrasApproved, row.currency)} extras`}
                        </span>
                      </span>
                      <span className="stack" style={{ alignItems: 'flex-end', gap: 4 }}>
                        <span
                          className={`badge ${
                            decided
                              ? decided.status === 'paid'
                                ? 'good'
                                : 'bad'
                              : !row.applicable
                                ? ''
                                : row.qualifies
                                  ? 'good'
                                  : 'bad'
                          }`}
                        >
                          {decided
                            ? decided.status === 'paid'
                              ? 'Paid'
                              : 'Withheld'
                            : !row.applicable
                              ? '—'
                              : row.qualifies
                                ? 'Qualifies'
                                : 'No'}
                        </span>
                        <span className="tiny muted">
                          {money(decided ? decided.amount : row.totalAmount, row.currency)}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {managing && <ExtrasManager children={children} currency={currency} onClose={() => setManaging(false)} />}

      {picked && (
        <PayoutSheet
          childId={picked.childId}
          row={picked.row}
          onClose={() => setPicked(null)}
          onSaved={() => {
            setPicked(null);
            void load();
          }}
        />
      )}
    </>
  );
}
